import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListBakvordurInOutStatusRes } from 'app/interfaces/responses/List/listBakvordurInOutStatusRes';
import { BakvordurService } from 'app/services/api/bakvordur/bakvordur.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-bakvordur',
    templateUrl: './bakvordur.component.html',
    styleUrls: ['./bakvordur.component.scss'],
})
export class BakvordurComponent implements OnInit, OnDestroy {

    private subs = new Subscription();

    public userDetails: ExposedReadUserDetailsRes = null;

    public searchInputControl = new FormControl('');

    public employees: ListBakvordurInOutStatusRes[] = [];
    public employeesIn: ListBakvordurInOutStatusRes[] = [];
    public searchedEmployees: ListBakvordurInOutStatusRes[] = [];

    public checkedInEmployeesCount = 0;
    public hasHttpFetched = false;
    public hasSearched = false;

    constructor(
        private bakvordurService: BakvordurService,
        private lsrauthService: LsrauthService
    ) {
        this.subs.add(
            this.lsrauthService.userDetails.subscribe((userDetails) => {
                this.userDetails = userDetails;

                if (this.hasBakvordurAdminRole && this.employees.length === 0) {
                    this.loadBakvordurInOutStatus();
                } else if (!this.hasBakvordurAdminRole && !this.hasHttpFetched) {
                    this.loadBakvordurInOutStatusCount();
                }
            })
        );
    }

    ngOnInit(): void {
        this.subs.add(
            this.searchInputControl.valueChanges.subscribe((searchValue) => {
                this.filterBakvordurInOutStatusEmployees(searchValue ?? '');
            })
        );
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    public get hasBakvordurAdminRole(): boolean {
        return this.userDetails?.roles?.some(role => role.roleName === USER_ROLES.BAKVORDUR_ADMIN) ?? false;
    }

    private loadBakvordurData(): void {
        if (this.hasBakvordurAdminRole) {
            this.loadBakvordurInOutStatus();
        } else {
            this.loadBakvordurInOutStatusCount();
        }
    }

    private loadBakvordurInOutStatus(): void {
        this.subs.add(
            this.bakvordurService.getBakvordurInOutStatus().subscribe((res) => {
                this.employees = res.returnItem ?? [];

                this.employeesIn = this.employees
                    .filter(employee => employee.io === 1)
                    .sort((a, b) =>
                        (a.name ?? '').localeCompare(b.name ?? '', 'is', { sensitivity: 'base' })
                    );
                this.searchedEmployees = this.employeesIn;
                this.checkedInEmployeesCount = this.employeesIn.length;
                this.hasHttpFetched = true;
            })
        );
    }

    private loadBakvordurInOutStatusCount(): void {
        this.subs.add(
            this.bakvordurService.getBakvordurInOutStatusCount().subscribe((res) => {
                this.checkedInEmployeesCount = res.returnItem ?? 0;
                this.hasHttpFetched = true;
            })
        );
    }

    private filterBakvordurInOutStatusEmployees(searchValue: string): void {
        const normalizedSearchValue = searchValue.toLowerCase().trim();

        if (!normalizedSearchValue) {
            this.searchedEmployees = this.employeesIn;
            return;
        }

        this.searchedEmployees = this.employeesIn.filter(employee =>
            employee.name?.toLowerCase().includes(normalizedSearchValue) ||
            employee.employeeNo?.toLowerCase().includes(normalizedSearchValue) ||
            employee.division?.toLowerCase().includes(normalizedSearchValue)
        );

        this.hasSearched = true;
    }

    public trackByBakvordurEmployeeJobId(index: number, employee: ListBakvordurInOutStatusRes): number {
        return employee.employeeJobId;
    }
}